import React, { useMemo, useState, useRef, useEffect } from 'react'
import { emotions_heads } from '../../../constants/Constants'
import { studentData } from '../../../constants/StudentAnalyticsConstants'
import Loading from '../../Loading'
import { timeSinceDays } from '../../../utils/util'
import { useTranslation } from 'react-i18next'
import InfoButton from '../../common/InfoButton'

function StudentCardData({ student, loading, onClassChange }) {
    return (
        <div>
            {loading && <LoadingContainer />}
            {!loading && !student && <LoadingContainer showEmptyData={true} />}
            {!loading && !!student && <DataContainer data={student} onClassChange={onClassChange} />}
        </div>
    )
}

export default StudentCardData

const DataContainer = ({ data, onClassChange }) => {
    const { t } = useTranslation()
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const dropdownRef = useRef(null)

    const backgroundColor = useMemo(() => {
        switch (data?.gender?.toLowerCase()) {
            case 'female':
                return 'bg-[#EED3FF]'
            case 'male':
                return 'bg-[#D3D5FF]'
            default:
                return 'bg-[#1f1f430D]'
        }
    }, [data?.gender])

    const hasMultipleClasses = data?.classes && Array.isArray(data.classes) && data.classes.length > 1

    const classes = useMemo(() => {
        if (data?.classes?.length > 1) {
            return [{ id: 'all', name: data.classesName }, ...data.classes]
        }
        return data.classes
    }, [data?.classes])

    const [selectedClass, setSelectedClass] = useState(classes?.[0] || null)

    const currentClass = hasMultipleClasses ? selectedClass : data?.classes?.[0] || data

    const handleClassSelect = (classItem) => {
        setSelectedClass(classItem)
        setIsDropdownOpen(false)
        console.log(onClassChange)

        // Notify parent component about class change
        if (onClassChange) {
            console.log('Change class', classItem)

            onClassChange(classItem)
        }
    }

    const currentTeacherData = useMemo(() => {
        return data?.teacher?.[currentClass?.id] || null
    }, [currentClass, data?.teacher])

    return (
        <div className={`py-8 px-8 rounded-[24px] relative ${backgroundColor}`} data-tour="basic-info">
            <div className="flex flex-row gap-5">
                <div className="flex items-center justify-center relative h-[110px] min-w-[110px]">
                    <div className="flex items-center justify-center bg-white rounded-full h-[90px] min-w-[90px]">
                        <img src={emotions_heads[data?.emotion]} width={65} height={65} alt="emotion" />
                        <button className="absolute right-0 top-0">
                            <InfoButton color="#77768D" text={t("This emotion shows student's last submitted mood check")} />
                        </button>
                    </div>
                </div>
                <div className="flex flex-col">
                    <p className="text-mediumGray text-[16px] leading-5 font-medium ">{t('student')}</p>
                    <p
                        className="text-darkenPurple text-[31px] leading-[39px] font-semibold tracking-tight mt-2 line-clamp-2 max-w-[210px]"
                        title={`${data?.first_name ?? ''} ${data?.last_name ?? ''}`}
                    >
                        {data?.first_name} {data?.last_name}
                    </p>
                    <p className={`text-strongGray text-[16px] leading-5 font-medium  mt-3 ${data?.emotion !== 'false' ? 'opacity-100' : 'opacity-0'}`}>
                        {t('Current Emotion')}: <span className="font-semibold capitalize ">{t(data?.emotion)}</span>
                    </p>
                </div>
            </div>
            <p className="text-strongGray text-[16px] leading-5 font-medium  mt-6">
                {t('Last online')}:{' '}
                {data?.lastLogin ? (
                    <span className="font-semibold ">
                        {/* {t('Logged')}  */}
                        {timeSinceDays(new Date(data?.lastLogin || data?.lastUsage || null))}
                    </span>
                ) : (
                    <span className="font-semibold ">-</span>
                )}
            </p>
            <div className="w-full h-[1px] bg-white mt-6"></div>
            <div className="bg-[#ffffff67] flex flex-row gap-8 p-5 rounded-[16px] mt-5 flex-1 relative">
                {/* // onMouseLeave={() => setIsDropdownOpen(false)}> */}
                <div className="flex flex-col gap-1 max-w-[80px]">
                    <p className="text-mediumGray text-[16px] leading-5 font-medium ">{t('class')}</p>
                    {hasMultipleClasses ? (
                        <div className="relative" ref={dropdownRef}>
                            <button
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                className="text-strongGray text-[20px] leading-[25px] font-semibold tracking-tight truncate flex items-center gap-1 hover:opacity-80 transition-opacity"
                                title={selectedClass?.name ?? ''}
                            >
                                {selectedClass?.name}
                                <svg className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            {isDropdownOpen && (
                                <div className={`absolute top-full -left-5 mt-1  rounded-[12px] shadow-lg z-50 min-w-[100px] max-h-[300px] overflow-y-auto ${backgroundColor}`}>
                                    {classes.map((classItem, index) => (
                                        <div key={classItem.id} className={`${backgroundColor} first:rounded-t-[12px] last:rounded-b-[12px]`}>
                                            <button
                                                onClick={() => handleClassSelect(classItem)}
                                                className={`w-full text-left px-4 py-3 text-[16px] font-medium hover:bg-gray-50 transition-colors bg-white ${
                                                    selectedClass?.name === classItem.name ? 'bg-[#ffffff67] text-darkenPurple' : 'text-mudPurple'
                                                }`}
                                                title={classItem.name}
                                            >
                                                <div className="flex flex-col">
                                                    <span className="font-semibold truncate">{classItem.name}</span>
                                                </div>
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <p className="text-strongGray text-[20px] leading-[25px] font-semibold tracking-tight truncate" title={currentClass?.name || data?.classesName || ''}>
                            {currentClass?.name || data?.classesName}
                        </p>
                    )}
                </div>
                <div className="flex flex-col gap-1 flex-1 max-w-[200px]">
                    <p className="text-mediumGray text-[16px] leading-5 font-medium ">{t('teacher')}</p>
                    <p
                        className="text-strongGray text-[20px] leading-[25px] font-semibold tracking-tight truncate flex-1"
                        title={`${currentTeacherData?.first_name} ${currentTeacherData?.last_name}`}
                    >
                        {currentTeacherData?.first_name} {currentTeacherData?.last_name}
                    </p>
                </div>
            </div>
        </div>
    )
}

const LoadingContainer = ({ showEmptyData = false }) => {
    const { t } = useTranslation()
    return (
        <div className="relative">
            <div className="blur">
                <DataContainer data={studentData} />
            </div>
            {showEmptyData ? (
                <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                    <div className="flex flex-col items-center">
                        <p className="text-[16px] leading-[25px] font-bold text-mudPurple ">{t('No data found')}</p>
                        <p className="text-[14px] leading-[25px] font-medium text-mudPurple px-5 text-center">
                            {t('This element will be updated once enough app-activity has been recorded')}
                        </p>
                    </div>
                </div>
            ) : (
                <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center z-10">
                    <Loading size={50} stroke={5} primaryColor="#7C82C3" secondaryColor="white" />
                </div>
            )}
        </div>
    )
}
